import { postApi, getApi, putApi, deleteApi } from '../utils/Api';
import { LoanCalculatorRequestSchemaContract } from '../schemas/LoanCalculatorRequestSchema';
import LoanCalculatorResponseSchema, { LoanCalculatorResponseSchemaContract } from '../schemas/LoanCalculatorResponseSchema';
import BasicUserLoanDetailsSchema, { BasicUserLoanDetailsSchemaContract } from '../schemas/BasicUserLoanDetailsSchema';
import { CreateLoanRequestSchemaContract } from '../schemas/CreateLoanRequestSchema';
import LoanDataSchema, { LoanDataSchemaContract } from '../schemas/LoanDataSchema';
import { FetchLoanDataSchemaContract } from '../schemas/FetchLoanDataSchema';
import LoanDataArraySchema, { LoanDataArraySchemaContract } from '../schemas/LoanDataArraySchema';
import LoanRecoveryPreviewResponseSchema, { LoanRecoveryPreviewResponseSchemaContract } from 'schemas/LoanRecoveryPreviewResponseSchema';
import { LoanEditRequestSchemaContract } from '../schemas/LoanEditRequestSchema';
import FullRecoveryAmountSchema, { FullRecoveryAmountSchemaContract } from '../schemas/FullRecoveryAmountSchema';
import { LoanSettingsResponse, LoanCategoriesResponse, LoanCategory, UploadPolicyResponse, CreateLoanCategoryRequest, UpdateLoanCategoryRequest } from 'components/Settings/EmployeeLoansSettings/types';
import { CreateEmployeeLoanRequest, EditEmployeeLoanRequest, EditPendingLoanTermsRequest, UpdateLoanStatusRequest, UpdateLoanSettingsRequest, ToggleLoanCategoryRequest, BulkDeleteLoansResponse } from 'components/LoansAndAdvances/types';



export default {
    basicUserLoanData: (peopleId: number, payrollMonth?: string) => {
        const params = new URLSearchParams();
        if (payrollMonth) params.set('payroll_month', payrollMonth);
        const query = params.toString() ? `?${params.toString()}` : '';
        return getApi<BasicUserLoanDetailsSchemaContract>({
            url: `/loans/basicUserData/${peopleId}${query}`,
            responseSchema: BasicUserLoanDetailsSchema
        })
    },
    createLoan: (requestData: CreateLoanRequestSchemaContract) => {
        return postApi<LoanDataSchemaContract, CreateLoanRequestSchemaContract>({
            url: `/loans/create`,
            requestData: requestData,
            responseSchema: LoanDataSchema
        })
    },
    fetchLoanData: (requestData?: FetchLoanDataSchemaContract) => {
        const searchParam = new URLSearchParams();

        if (requestData?.user_id) {
            searchParam.set('user_id', requestData.user_id.toString());
        }

        if (requestData?.statuses && (requestData.statuses as number[]).length > 0) {
            (requestData.statuses as number[]).forEach((s) => searchParam.append('status[]', s.toString()));
        } else if (requestData?.status) {
            searchParam.set('status', requestData.status.toString());
        }

        if (requestData?.page) {
            searchParam.set('page', requestData.page.toString());
        }

        if (requestData?.loan_id) {
            searchParam.set('loan_id', requestData.loan_id.toString());
        }

        if (requestData?.limit) {
            searchParam.set('limit', requestData.limit.toString());
        }

        if (requestData?.created_from) {
            searchParam.set('created_from', requestData.created_from.toString());
        }

        if (requestData?.created_to) {
            searchParam.set('created_to', requestData.created_to.toString());
        }

        const url = '/loans/fetch?' + searchParam.toString();

        return getApi<LoanDataArraySchemaContract>({
            url: url,
            responseSchema: LoanDataArraySchema
        })
    },
    fetchEmployeeLoanData: (requestData?: FetchLoanDataSchemaContract) => {
        const searchParam = new URLSearchParams();

        if (requestData?.statuses?.length) {
            requestData.statuses.forEach((s) => searchParam.append('status[]', s.toString()));
        } else if (requestData?.status) {
            searchParam.set('status', requestData.status.toString());
        }
        if (requestData?.page) searchParam.set('page', requestData.page.toString());
        if (requestData?.limit) searchParam.set('limit', requestData.limit.toString());
        if (requestData?.loan_id) searchParam.set('loan_id', requestData.loan_id.toString());
        if (requestData?.loan_category_name) searchParam.set('loan_category_name', requestData.loan_category_name.toString());

        return getApi<LoanDataArraySchemaContract>({
            url: '/loans/employee?' + searchParam.toString(),
            responseSchema: LoanDataArraySchema,
        });
    },
    calculateLoan: (requestData: LoanCalculatorRequestSchemaContract) => {
        return postApi<LoanCalculatorResponseSchemaContract, LoanCalculatorRequestSchemaContract>({
            url: `/loans/calculateLoan`,
            requestData: requestData,
            responseSchema: LoanCalculatorResponseSchema
        })
    },

    recoveryPreview: (loanId: number, action?: string, option?: number, amount?: number, tenure?: number, payroll_month?: string) => {
        const queryParams = new URLSearchParams();

        action && queryParams.append('action', action);
        option && queryParams.append('option', option.toString());
        amount && queryParams.append('amount', amount.toString());
        tenure && queryParams.append('tenure', tenure.toString());
        payroll_month && queryParams.append('payroll_month', payroll_month);

        var url = `loans/${loanId}/recovery-preview?${queryParams.toString()}`;

        return getApi<LoanRecoveryPreviewResponseSchemaContract>({
            url: url,
            responseSchema: {},
        })
    },
    editLoan: (requestData: LoanEditRequestSchemaContract, loanId: number) => {
        var url = `loans/${loanId}/edit`;

        return postApi<LoanDataSchemaContract, LoanEditRequestSchemaContract>({
            url: url,
            requestData: requestData,
            responseSchema: LoanDataSchema,
        })
    },
    getFullRecoveryAmountForLoan: (loanId: number) => {
        return getApi<FullRecoveryAmountSchemaContract>({
            url: `/loans/${loanId}/full-recovery`,
            responseSchema: FullRecoveryAmountSchema
        })
    },
    getLoanSettings: () => {
        return getApi<LoanSettingsResponse>({
            url: `/loan-settings`,
            responseSchema: {},
        });
    },
    updateLoanSettings: (payload: UpdateLoanSettingsRequest) => {
        return postApi<{ success: boolean }, UpdateLoanSettingsRequest>({
            url: `/loan-settings`,
            requestData: payload,
            responseSchema: {},
        });
    },
    listLoanCategories: (page?: number, limit?: number) => {
        const queryParams = new URLSearchParams();
        page && queryParams.set('page', page.toString());
        limit && queryParams.set('limit', limit.toString());
        return getApi<LoanCategoriesResponse>({
            url: `/loan-categories?${queryParams.toString()}`,
            responseSchema: {},
        });
    },
    uploadLoanCategoryPolicy: (file: File) => {
        const formData = new FormData();
        formData.append('policy', file);
        return postApi<UploadPolicyResponse, FormData>(
            {
                url: `/loan-categories/upload-policy`,
                requestData: formData,
                responseSchema: {},
            },
            'multipart',
        );
    },
    createLoanCategory: (requestData: CreateLoanCategoryRequest) => {
        return postApi<LoanCategory, CreateLoanCategoryRequest>({
            url: `/loan-categories`,
            requestData,
            responseSchema: {},
        });
    },
    updateLoanCategory: ({ categoryId, ...requestData }: UpdateLoanCategoryRequest & { categoryId: number }) => {
        return putApi<LoanCategory, UpdateLoanCategoryRequest>({
            url: `/loan-categories/${categoryId}`,
            requestData,
            responseSchema: {},
        });
    },
    listEmployeeLoanCategories: () => {
        return getApi<LoanCategoriesResponse>({
            url: `/employee/loan-categories`,
            responseSchema: {},
        });
    },
    createEmployeeLoan: (requestData: CreateEmployeeLoanRequest) => {
        return postApi<{ id: number; status: string }, CreateEmployeeLoanRequest>({
            url: `/loans/employee/create`,
            requestData,
            responseSchema: {},
        });
    },
    uploadLoanDocument: (file: File) => {
        const formData = new FormData();
        formData.append('document', file);
        return postApi<{ file_id: string }, FormData>(
            { url: `/loans/upload-document`, requestData: formData, responseSchema: {} },
            'multipart',
        );
    },
    toggleLoanCategory: ({ categoryId, isActive }: ToggleLoanCategoryRequest) => {
        return postApi<{ success: boolean }, { is_active: boolean }>({
            url: `/loan-categories/${categoryId}/toggle`,
            requestData: { is_active: isActive },
            responseSchema: {},
        });
    },

    disburseLoans: () => {
        return postApi<{ success: boolean }, {}>({
            url: `/loans/disburseLoans`,
            requestData: {},
            responseSchema: {},
        });
    },

    editEmployeeLoan: (loanId: number, requestData: EditEmployeeLoanRequest) => {
        return putApi<LoanCategory, EditEmployeeLoanRequest>({
            url: `/loans/employee/${loanId}`,
            requestData,
            responseSchema: {},
        });
    },

    withdrawLoan: (loanId: number) => {
        return deleteApi<{ success: boolean }, {}>({
            url: `/loans/employee/${loanId}`,
            requestData: {},
            responseSchema: {},
        });
    },
    deleteLoan: (loanId: number) => {
        return deleteApi<{ success: boolean }, {}>({
            url: `/loans/${loanId}`,
            requestData: {},
            responseSchema: {},
        });
    },
    bulkDeleteLoans: (loanIds: number[]) => {
        return deleteApi<BulkDeleteLoansResponse, { loan_ids: number[] }>({
            url: `/loans`,
            requestData: { loan_ids: loanIds },
            responseSchema: {},
        });
    },

    editPendingLoanTerms: (loanId: number, requestData: EditPendingLoanTermsRequest) => {
        return putApi<LoanCategory, EditPendingLoanTermsRequest>({
            url: `/loans/${loanId}/edit`,
            requestData,
            responseSchema: {},
        });
    },
    updateLoanStatus: (loanId: number, requestData: UpdateLoanStatusRequest) => {
        return postApi<LoanCategory, UpdateLoanStatusRequest>({
            url: `/loans/${loanId}/update-status`,
            requestData,
            responseSchema: {},
        });
    },
    markLoansAsPaid: (loanIds: number[]) => {
        return postApi<{ marked_count: number }, { loan_ids: number[] }>({
            url: `/loans/mark-as-paid`,
            requestData: { loan_ids: loanIds },
            responseSchema: {},
        });
    },
    getLoanDetail: (loanId: number) => {
        return getApi<any>({
            url: `/loans/${loanId}`,
            responseSchema: {},
        });
    },
    getLoanCategory: (categoryId: number) => {
        return getApi<LoanCategory & { policy_document_url?: string }>({
            url: `/loan-categories/${categoryId}`,
            responseSchema: {},
        });
    },
    deleteLoanCategory: (categoryId: number) => {
        return deleteApi<{ success: boolean }, {}>({
            url: `/loan-categories/${categoryId}`,
            requestData: {},
            responseSchema: {},
        });
    },
    downloadLoanDocument: (fileId: string) => {
        return getApi<{ signed_url: string }>({
            url: `/loans/download/${fileId}`,
            responseSchema: {},
        });
    },
}
